// src/components/project-settings/components/LayerSidebar/LightingStandardsInfo.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import { X as CloseIcon } from 'lucide-react';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`standards-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const mClassData = [
  {
    class: 'M1',
    luminance: '2.00',
    uniformity: '0.40',
    longitudinal: '0.70',
    wetUniformity: '0.15',
    increment: '10',
    ratio: '0.35',
  },
  {
    class: 'M2',
    luminance: '1.50',
    uniformity: '0.40',
    longitudinal: '0.70',
    wetUniformity: '0.15',
    increment: '10',
    ratio: '0.35',
  },
  {
    class: 'M3',
    luminance: '1.00',
    uniformity: '0.40',
    longitudinal: '0.60',
    wetUniformity: '0.15',
    increment: '15',
    ratio: '0.30',
  },
  {
    class: 'M4',
    luminance: '0.75',
    uniformity: '0.40',
    longitudinal: '0.60',
    wetUniformity: '0.15',
    increment: '15',
    ratio: '0.30',
  },
  {
    class: 'M5',
    luminance: '0.50',
    uniformity: '0.35',
    longitudinal: '0.40',
    wetUniformity: '0.15',
    increment: '15',
    ratio: '0.30',
  },
  {
    class: 'M6',
    luminance: '0.30',
    uniformity: '0.35',
    longitudinal: '0.40',
    wetUniformity: '0.15',
    increment: '20',
    ratio: '0.30',
  },
];

const cClassData = [
  { class: 'C0', illuminance: '50.00', uniformity: '0.40', increment: '0.15' },
  { class: 'C1', illuminance: '30.0', uniformity: '0.40', increment: '15' },
  { class: 'C2', illuminance: '20.0', uniformity: '0.40', increment: '15' },
  { class: 'C3', illuminance: '15.0', uniformity: '0.40', increment: '20' },
  { class: 'C4', illuminance: '10.00', uniformity: '0.40', increment: '0.20' },
  { class: 'C5', illuminance: '7.50', uniformity: '0.40', increment: '0.20' },
];

const pClassData = [
  {
    class: 'P1',
    avgHorizontal: '15.0',
    minHorizontal: '3.00',
    increment: '20',
    minVertical: '5.0',
    minSemiCylindrical: '5.0',
  },
  {
    class: 'P2',
    avgHorizontal: '10.0',
    minHorizontal: '2.00',
    increment: '25',
    minVertical: '3.0',
    minSemiCylindrical: '2.0',
  },
  {
    class: 'P3',
    avgHorizontal: '7.50',
    minHorizontal: '1.50',
    increment: '25',
    minVertical: '2.5',
    minSemiCylindrical: '1.5',
  },
  {
    class: 'P4',
    avgHorizontal: '5.00',
    minHorizontal: '1.00',
    increment: '0.30',
    minVertical: '1.50',
    minSemiCylindrical: '1.00',
  },
  {
    class: 'P5',
    avgHorizontal: '3.00',
    minHorizontal: '0.60',
    increment: '0.30',
    minVertical: '1.00',
    minSemiCylindrical: '0.60',
  },
  {
    class: 'P6',
    avgHorizontal: '2.00',
    minHorizontal: '0.40',
    increment: '0.35',
    minVertical: '0.60',
    minSemiCylindrical: '0.20',
  },
];

const hsClassData = [
  { class: 'HS1', uniformity: '0.15', horizontalIlluminance: '5.00' },
  { class: 'HS2', uniformity: '0.15', horizontalIlluminance: '2.50' },
  { class: 'HS3', uniformity: '0.15', horizontalIlluminance: '1.00' },
];

export const LightingStandardsInfo: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      {/* Norms Specification Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleOpen();
        }}
        className="px-4 py-2 text-xs border border-white rounded text-white hover:border-lime-400 hover:text-lime-400 transition"
      >
        {t('workspace.layersTab.normsSpecification')}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'black',
            color: 'white',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
          marginTop: '100px',
          zIndex: 9999,
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {t('workspace.lightingStandardsInfo.title')}
            <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={t('workspace.lightingStandardsInfo.tabs.mClasses')} />
              <Tab label={t('workspace.lightingStandardsInfo.tabs.cClasses')} />
              <Tab label={t('workspace.lightingStandardsInfo.tabs.pClasses')} />
              <Tab label={t('workspace.lightingStandardsInfo.tabs.hsClasses')} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="body1" paragraph sx={{ color: 'white' }}>
              {t('workspace.lightingStandardsInfo.mClass.description')}
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.class')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.averageLuminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.overallUniformity')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.longitudinalUniformity')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.overallUniformityWet')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.thresholdIncrement')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.edgeIlluminanceRatio')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mClassData.map((row) => (
                    <TableRow key={row.class}>
                      <TableCell sx={{ color: 'white' }}>{row.class}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.luminance}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.uniformity}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.longitudinal}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.wetUniformity}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.increment}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.ratio}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="body1" paragraph sx={{ color: 'white' }}>
              {t('workspace.lightingStandardsInfo.cClass.description')}
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.class')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.averageIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.uniformityOfIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.thresholdIncrement')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cClassData.map((row) => (
                    <TableRow key={row.class}>
                      <TableCell sx={{ color: 'white' }}>{row.class}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.illuminance}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.uniformity}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.increment}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="body1" paragraph sx={{ color: 'white' }}>
              {t('workspace.lightingStandardsInfo.pClass.description')}
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.class')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.avgHorizontalIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.minHorizontalIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.thresholdIncrement')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.minVerticalIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.minSemiCylindricalIlluminance')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pClassData.map((row) => (
                    <TableRow key={row.class}>
                      <TableCell sx={{ color: 'white' }}>{row.class}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.avgHorizontal}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.minHorizontal}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.increment}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.minVertical}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.minSemiCylindrical}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 2 }}>
              {t('workspace.lightingStandardsInfo.pClass.note')}
            </Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="body1" paragraph sx={{ color: 'white' }}>
              {t('workspace.lightingStandardsInfo.hsClass.description')}
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.class')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.uniformityOfIlluminance')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {t('workspace.lightingStandardsInfo.tableHeaders.horizontalIlluminance')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hsClassData.map((row) => (
                    <TableRow key={row.class}>
                      <TableCell sx={{ color: 'white' }}>{row.class}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.uniformity}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{row.horizontalIlluminance}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </>
  );
};
